import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose, setDisplayName } from 'recompact';

import type { UnifiedEvent } from 'src/app/analytics/analyticsEvents';
import { errorBoundary } from 'src/app/hocs';
import { segmentTrack } from 'src/app/state/actions';
import {
  loadCartDeliveryMethods,
  loadCartDeliveryShippingOptions,
  updateCartDeliveryMethod,
  updateCartPaymentMethod,
} from 'src/app/state/actions/cart';
import { pushNextCheckoutState } from 'src/app/state/actions/checkout';
import {
  getAccountDefaultCardId,
  getBFFCartDeliveryMethodNames,
  getBFFCartDeliveryMethods,
  getBFFCartDeliveryMethodsIsFetching,
  getCurrentCartId,
  getCurrentCartUUID,
  getIsBFFCartDeliveryMethodsFetched,
} from 'src/app/state/selectors';
import type { Dispatch, RootState } from 'src/app/state/store';
import type { UpdateCartPaymentMethodRequest } from 'src/app/types';
import { DeliveryMethodType } from 'src/consts';

import Renderer from '.';

function makeMapStateToProps() {
  return (state: RootState) => {
    const cartId = getCurrentCartId(state);
    const cartUUID = getCurrentCartUUID(state);
    const bffCartDeliveryMethods = getBFFCartDeliveryMethods(state);

    const defaultCardId = getAccountDefaultCardId(state);
    const deliveryMethodNames = getBFFCartDeliveryMethodNames(state);
    const isFetchedBFFCartDeliveryMethods = getIsBFFCartDeliveryMethodsFetched(state);
    const isFetchingBFFCartDeliveryMethods = getBFFCartDeliveryMethodsIsFetching(state);

    return {
      cartId,
      cartUUID,
      deliveryMethodNames,
      bffCartDeliveryMethods,
      defaultCardId,
      isFetchedBFFCartDeliveryMethods,
      isFetchingBFFCartDeliveryMethods,
    };
  };
}

const mapDispatchToProps = (dispatch: Dispatch) => ({
  dispatchLoadCartDeliveryMethods: async () => await dispatch(loadCartDeliveryMethods()),
  dispatchUpdateCartAndPushNextCheckoutState: async (
    cartUUID: string,
    paymentMethodBody: UpdateCartPaymentMethodRequest
  ) => {
    const shippingOptions = await dispatch(loadCartDeliveryShippingOptions(DeliveryMethodType.mail));
    const firstShippingOptionKey = shippingOptions?.length > 0 ? shippingOptions[0].key : null;
    await dispatch(
      updateCartDeliveryMethod({ cartUUID, shippingOption: firstShippingOptionKey, type: DeliveryMethodType.mail })
    );
    // If a default card id is already present, we automatically update the cart with that to save time (from product).
    // The user can select a different card by clicking the payment section in the checkout summary screen.
    if (paymentMethodBody.paymentMethodId) await dispatch(updateCartPaymentMethod(paymentMethodBody));
    await dispatch(pushNextCheckoutState());
  },
  dispatchSegmentTrack: (event: UnifiedEvent, payload) => dispatch(segmentTrack(event, payload)),
});

const connector = connect(makeMapStateToProps, mapDispatchToProps);
export type PropsFromRedux = ConnectedProps<typeof connector>;

export default compose(errorBoundary, setDisplayName('DeliveryMethods'), connector)(Renderer);
